import { useState } from 'react';
import { reportsApi } from '../services/api/endpoints/reports';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../stores/businessUnitStore';
import { useAuthStore } from '../stores/authStore';
import type { ReportMetadata } from '../utils/reportAdapters';
import type { ReportGenerationParams } from '../types/reporting-filters';

export function useReports() {
  const [data, setData] = useState<any>(null);
  const [metadata, setMetadata] = useState<Partial<ReportMetadata> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentFiscalYear } = useFiscalYearStore();
  const { selectedBusinessUnit, effectiveFilter } = useBusinessUnitStore();
  const { organization } = useAuthStore();

  // companyId requis par le backend pour l'isolation des données
  const companyId = organization?.id;
  // fiscalYear UUID requis par le backend pour tous les rapports
  const fiscalYearId = currentFiscalYear?.id ?? '';

  const generateReport = async (params: ReportGenerationParams) => {
    setLoading(true);
    setError(null);

    // Utiliser les dates de l'exercice comptable courant si disponibles
    const fiscalStartDate = currentFiscalYear?.startDate;
    const fiscalEndDate = currentFiscalYear?.endDate;
    const effectiveStartDate = params.startDate || fiscalStartDate || new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0];
    const effectiveEndDate = params.endDate || fiscalEndDate || new Date().toISOString().split('T')[0];
    const effectiveDate = params.date || fiscalEndDate || new Date().toISOString().split('T')[0];

    // BU filtering: single ID pour isolation, IDs[] pour consolidation hiérarchique
    const buId = effectiveFilter.businessUnitId;
    const buIds = effectiveFilter.businessUnitIds && effectiveFilter.businessUnitIds.length > 1
      ? effectiveFilter.businessUnitIds.join(',')
      : undefined;

    try {
      // Try API first if online
      if (navigator.onLine) {
        try {
          // Utiliser les endpoints GET spécifiques selon le type de rapport
          // C'est plus RESTful et permet au backend d'optimiser chaque type
          let apiResponse;
          
          switch (params.type) {
            case 'balance':
              // GET /reports/balance-sheet
              apiResponse = await reportsApi.getBalanceSheet({
                date: effectiveDate,
                fiscalYear: params.fiscalYear || fiscalYearId,
                comparative: params.comparative,
                currency: params.currency,
                companyId,
                businessUnitId: buId,
                ...(buIds && { businessUnitIds: buIds }),
              });
              break;
              
            case 'income':
              // GET /reports/income-statement
              apiResponse = await reportsApi.getIncomeStatement({
                startDate: effectiveStartDate,
                endDate: effectiveEndDate,
                fiscalYear: params.fiscalYear || fiscalYearId,
                comparative: params.comparative,
                currency: params.currency,
                companyId,
                businessUnitId: buId,
                ...(buIds && { businessUnitIds: buIds }),
              });
              break;
              
            case 'cashflow':
              // GET /reports/cash-flow
              apiResponse = await reportsApi.getCashFlowStatement({
                startDate: effectiveStartDate,
                endDate: effectiveEndDate,
                fiscalYear: params.fiscalYear || fiscalYearId,
                comparative: params.comparative,
                currency: params.currency,
                companyId,
                businessUnitId: buId,
                ...(buIds && { businessUnitIds: buIds }),
              });
              break;
              
            case 'trial-balance':
              // GET /reports/trial-balance
              apiResponse = await reportsApi.getTrialBalance({
                date: effectiveDate,
                fiscalYear: params.fiscalYear || fiscalYearId,
                includeOpeningBalance: params.includeOpeningBalance ?? true,
                openingDate: params.openingDate || fiscalStartDate,
                groupByClass: params.groupByClass,
                companyId,
                businessUnitId: buId,
                ...(buIds && { businessUnitIds: buIds }),
              });
              break;
              
            case 'equity-changes':
              // GET /reports/equity-changes
              apiResponse = await reportsApi.getEquityChanges({
                startDate: effectiveStartDate,
                endDate: effectiveEndDate,
                currency: params.currency,
                fiscalYear: params.fiscalYear || fiscalYearId,
                companyId,
                businessUnitId: buId,
                ...(buIds && { businessUnitIds: buIds }),
              });
              break;
              
            case 'notes':
              // GET /reports/notes
              apiResponse = await reportsApi.getNotes({
                date: effectiveDate,
                type: 'all',
                fiscalYear: params.fiscalYear || fiscalYearId,
                companyId,
                businessUnitId: buId,
                ...(buIds && { businessUnitIds: buIds }),
              });
              break;
              
            default:
              // Pour les autres types (reconciliation, analytical, social, statistics)
              // utiliser l'endpoint générique POST /reports/generate
              const generatePayload: any = {
                type: params.type,
                format: params.format || 'json',
                date: effectiveDate,
                startDate: effectiveStartDate,
                endDate: effectiveEndDate,
                comparative: params.comparative,
                currency: params.currency,
                includeNotes: params.includeNotes,
                companyId
              };
              
              if (buId || params.filters) {
                generatePayload.filters = {
                  ...params.filters,
                  ...(buId && { business_unit_id: buId }),
                  ...(buIds && { business_unit_ids: buIds }),
                };
              }
              
              apiResponse = await reportsApi.generateReport(generatePayload);
          }
          
          if (apiResponse?.success && apiResponse.data) {
            console.log('📊 API Response received:', { 
              success: apiResponse.success, 
              dataKeys: apiResponse.data ? Object.keys(apiResponse.data) : null,
              type: params.type
            });
            
            // Passer la donnée brute directement — FinancialStatementPreview gère le format natif
            const rawData = apiResponse.data;
            const meta = rawData?.metadata;
            setData(rawData);
            setMetadata({
              fiscalYear:     meta?.fiscalYear     ?? '',
              currency:       meta?.currency       ?? 'CDF',
              currencySymbol: meta?.currencySymbol ?? 'FC',
              framework:      meta?.framework      ?? 'SYSCOHADA',
              generatedAt:    meta?.generatedAt,
            } as ReportMetadata);
            return;
          } else {
            console.warn('⚠️ API Response not successful or no data:', apiResponse);
            throw new Error('Le serveur n\'a pas retourné de données valides');
          }
        } catch (apiError) {
          console.error('❌ API call failed:', apiError);
          throw apiError;
        }
      } else {
        throw new Error('Vous êtes hors ligne. Veuillez vérifier votre connexion internet.');
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue lors de la génération du rapport');
    } finally {
      setLoading(false);
    }
  };

  return {
    data,
    metadata, // Métadonnées du rapport (company, fiscalYear, generatedDate, etc.)
    loading,
    error,
    generateReport,
    exportReport: async (type: any, format: 'pdf' | 'excel', reportData: any) => {
      try {
        setLoading(true);
        const response = await reportsApi.exportReport({
          type,
          format,
          data: reportData
        });
        if (response.success && response.data) {
          // Create download link for blob
          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${type}-report.${format}`;
          link.click();
          window.URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error('Error exporting report:', err);
        setError(err instanceof Error ? err.message : 'Erreur lors de l\'export');
      } finally {
        setLoading(false);
      }
    },
    searchReports: async (searchParams: any) => {
      try {
        setLoading(true);
        const response = await reportsApi.searchReports(searchParams);
        return response.success ? response.data : [];
      } catch (err) {
        console.error('Error searching reports:', err);
        return [];
      } finally {
        setLoading(false);
      }
    }
  };
}